import { Box } from '@mui/material'
import { DUR, EASE, FS, focusRing, hoverOnly } from '../../theme/tokens'

/**
 * SegmentedSlider — iOS-style segmented control with an animated pill slider.
 *
 * @description
 *   One row of N buttons inside a rounded container. The selected option is
 *   highlighted by a single pill that slides between slots via CSS transform.
 *   Controlled component — parent owns the value.
 *
 *   A `value` that matches NO option is a real state, not a fallback to the
 *   first option: the pill is hidden and every `aria-selected` is false, so the
 *   control shows "nothing chosen yet" instead of claiming an answer the
 *   operator never gave. Rule 22 No. 3 — unknown is never `0`.
 *
 * @useWhen
 *   - Mutually-exclusive option selector with 2-5 choices
 *   - Direction / view / mode switcher where active state must be obvious
 *   - Step indicator in a wizard (visual is better than radio buttons)
 *   - Filter tabs with a small fixed option set
 *
 * @dontUseFor
 *   - Multi-select — use BottomSheetMultiSelect (mobile) or FilterPillRow (multi mode)
 *   - More than 5 options — use BottomSheetSelect (dropdown) instead
 *   - Single boolean toggle — use MUI Switch
 *   - Navigation tabs tied to URL routes — use UnderlineTabs
 *
 * @related
 *   - UnderlineTabs — navigation/route tabs with underline indicator
 *   - FilterPillRow — scrollable pill filters, supports multi-select
 *   - BottomSheetSelect — dropdown for >5 single-select options
 *
 * @reuseHints
 *   - Inquiry flow step indicator (Draft / Review / Sent)
 *   - List view layout switcher (Grid / Table / Compact)
 *   - PO status filter (Draft / Sent / Confirmed) if narrowed ≤5
 *   - Advisor result mode toggle (Summary / Details / Raw)
 *
 * @props
 *   value       — currently-selected option value. A value that matches no
 *                 option renders the control WITHOUT a selection (no pill,
 *                 aria-selected false everywhere) — use '' or null for
 *                 "not answered yet"
 *   onChange    — (newValue) => void
 *   options     — [{ value, label, disabled? }]
 *   size        — 'small' (default) | 'medium'
 *   fullWidth   — default true
 *   disabled    — disables all options
 *   sx          — extra sx merged onto the container
 *
 * Example:
 *   <SegmentedSlider
 *     value={direction}
 *     onChange={setDirection}
 *     options={[
 *       { value: 'out',    label: 'Us → Customer' },
 *       { value: 'in',     label: 'Vendor → Us' },
 *       { value: 'vendor', label: 'Us → Vendor' },
 *     ]}
 *   />
 */
export default function SegmentedSlider({
  value,
  onChange,
  options = [],
  size = 'small',
  fullWidth = true,
  disabled = false,
  sx,
}) {
  const count = options.length
  // -1 heisst „keine Antwort": der Wert passt auf KEINE Option. Frueher stand
  // hier Math.max(0, …), damit sass die Pille auf dem ERSTEN Feld und die
  // Oberflaeche behauptete eine Auswahl, die niemand getroffen hatte
  // (Regel-16-Blocker B1, N57 Schnitt B, 2026-09-30).
  const activeIdx = options.findIndex(o => o.value === value)
  const hasSelection = activeIdx >= 0
  const pad = 4   // inner padding around the sliding pill
  const buttonPadY = size === 'small' ? 8 : 11
  const fontSize = size === 'small' ? FS.sm : FS.base

  return (
    <Box
      role="tablist"
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: `repeat(${count}, 1fr)`,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 99,
        p: `${pad}px`,
        width: fullWidth ? '100%' : 'auto',
        overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'default',
        // Sliding pill — hidden while nothing is selected
        '&::before': {
          content: '""',
          position: 'absolute',
          top: `${pad}px`,
          left: `${pad}px`,
          width: `calc((100% - ${pad * 2}px) / ${count})`,
          height: `calc(100% - ${pad * 2}px)`,
          bgcolor: 'primary.main',
          borderRadius: 99,
          transform: `translateX(${Math.max(0, activeIdx) * 100}%)`,
          // K-T1: 0.35 s waren ueber der Haus-Obergrenze von 300 ms. Die Kurve
          // ist unveraendert (`EASE.standard` ist genau `cubic-bezier(0.4, 0,
          // 0.2, 1)`), nur die Dauer kommt jetzt aus `DUR.ui`.
          transition: `transform ${DUR.ui}ms ${EASE.standard}`,
          display: hasSelection ? 'block' : 'none',
          zIndex: 0,
        },
        ...sx,
      }}
    >
      {options.map((opt) => {
        const isActive = opt.value === value
        const isDisabled = disabled || opt.disabled
        return (
          <Box
            key={opt.value}
            component="button"
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={isDisabled}
            onClick={() => { if (!isDisabled) onChange?.(opt.value) }}
            sx={{
              position: 'relative',
              zIndex: 1,
              bgcolor: 'transparent',
              border: 'none',
              color: isActive ? 'primary.contrastText' : 'text.secondary',
              fontWeight: isActive ? 600 : 400,
              fontFamily: 'inherit',
              fontSize,
              py: `${buttonPadY}px`,
              px: 1,
              borderRadius: 99,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              transition: 'color 0.25s, font-weight 0.25s',
              // K-5 (Welle 2): reine Wirkung (Farbe) ohne Hintergrund — auch die
              // BEDINGTE Form klebt auf einem groben Zeiger.
              ...hoverOnly(isActive || isDisabled ? {} : { color: 'text.primary' }),
              '&:focus-visible': focusRing(),
            }}
          >
            {opt.label}
          </Box>
        )
      })}
    </Box>
  )
}
